/**
 * Post Actions Bar — Like, Comment, Repost, Share + Views count.
 * Like has optimistic update + haptic feedback + scale animation.
 */

import { memo, useCallback } from 'react'
import { Heart, MessageCircle, Repeat2, Share2, Eye } from 'lucide-react'
import { motion } from 'framer-motion'
import { triggerHapticImpact as hapticImpact } from '@turnifly/shared/utils'

interface PostActionsBarProps {
  likesCount: number
  commentsCount: number
  repostsCount: number
  viewsCount: number
  isLiked: boolean
  onLike: () => void
  onComment: () => void
  onRepost: () => void
  onShare: () => void
}

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return String(n)
}

export const PostActionsBar = memo(function PostActionsBar({
  likesCount, commentsCount, repostsCount, viewsCount,
  isLiked, onLike, onComment, onRepost, onShare,
}: PostActionsBarProps): React.ReactElement {
  const handleLike = useCallback((): void => {
    hapticImpact('light')
    onLike()
  }, [onLike])

  return (
    <div className="flex items-center gap-1 -ml-2">
      {/* Like */}
      <button
        onClick={handleLike}
        className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg tap-highlight-transparent hover:bg-white/[0.04] transition-colors"
      >
        <motion.div
          animate={isLiked ? { scale: [1, 1.3, 1] } : {}}
          transition={{ duration: 0.3 }}
        >
          <Heart
            size={19}
            className={isLiked
              ? 'text-red-500 fill-red-500'
              : 'text-text-hint/40 hover:text-text-hint/60'
            }
          />
        </motion.div>
        {likesCount > 0 && (
          <span className={`text-[12px] tabular-nums ${
            isLiked ? 'text-red-500 font-medium' : 'text-text-hint/40'
          }`}>
            {formatCount(likesCount)}
          </span>
        )}
      </button>

      {/* Comment */}
      <button
        onClick={onComment}
        className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg tap-highlight-transparent hover:bg-white/[0.04] transition-colors"
      >
        <MessageCircle size={19} className="text-text-hint/40 hover:text-text-hint/60" />
        {commentsCount > 0 && (
          <span className="text-[12px] text-text-hint/40 tabular-nums">{formatCount(commentsCount)}</span>
        )}
      </button>

      {/* Repost */}
      <button
        onClick={onRepost}
        className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg tap-highlight-transparent hover:bg-white/[0.04] transition-colors"
      >
        <Repeat2 size={19} className="text-text-hint/40 hover:text-text-hint/60" />
        {repostsCount > 0 && (
          <span className="text-[12px] text-text-hint/40 tabular-nums">{formatCount(repostsCount)}</span>
        )}
      </button>

      {/* Views — read-only */}
      {viewsCount > 0 && (
        <div className="flex items-center gap-1 px-2 py-1.5">
          <Eye size={16} className="text-text-hint/25" />
          <span className="text-[11px] text-text-hint/25 tabular-nums">{formatCount(viewsCount)}</span>
        </div>
      )}

      {/* Share — pushed to right */}
      <button
        onClick={onShare}
        className="ml-auto px-2 py-1.5 rounded-lg tap-highlight-transparent hover:bg-white/[0.04] transition-colors"
      >
        <Share2 size={17} className="text-text-hint/40 hover:text-text-hint/60" />
      </button>
    </div>
  )
})
